// Turns the Studio API's outcome_projection_unavailable body (comma-separated
// regulation lists; applyKambiResolution in post_service.go) into
// regulation-level copy.
const OUTCOME_PROJECTION_UNAVAILABLE = 'outcome_projection_unavailable'

export const REGULATION_NOTICES = {
  unavailable: {
    label: 'Not offered',
    tone: 'warning',
    description: 'This regulation does not offer the attached market.',
  },
  unconfirmed: {
    label: 'Not confirmed',
    tone: 'neutral',
    description: 'The sportsbook could not be reached for this regulation. Try again, or publish without it.',
  },
}

function splitOfferingList(value) {
  return String(value || '')
    .split(',')
    .map((offeringId) => offeringId.trim())
    .filter(Boolean)
}

function joinLabels(labels) {
  if (labels.length <= 1) return labels[0] || ''
  return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`
}

// Returns null unless the error names at least one regulation: a coded error
// with no lists has nothing regulation-specific to show and stays generic.
export function parseRegulationAvailabilityError(error) {
  const data = error?.response?.data
  if (!data || data.code !== OUTCOME_PROJECTION_UNAVAILABLE) return null
  const available = splitOfferingList(data.available_offerings)
  const unavailable = splitOfferingList(data.unavailable_offerings)
  const unconfirmed = splitOfferingList(data.unconfirmed_offerings)
  if (unavailable.length === 0 && unconfirmed.length === 0) return null
  return {
    organizationSlug: data.organization_slug || '',
    available,
    unavailable,
    unconfirmed,
    partialAllowed: data.partial_publish_allowed === 'true' && available.length > 0,
  }
}

// "Virginia and Arizona do not offer this market. Ohio could not be confirmed."
export function describeRegulationShortfall({ unavailable = [], unconfirmed = [] } = {}, labelFor = String) {
  const sentences = []
  if (unavailable.length > 0) {
    const verb = unavailable.length === 1 ? 'does' : 'do'
    sentences.push(`${joinLabels(unavailable.map(labelFor))} ${verb} not offer this market.`)
  }
  if (unconfirmed.length > 0) {
    sentences.push(`${joinLabels(unconfirmed.map(labelFor))} could not be confirmed.`)
  }
  return sentences.join(' ')
}

// An additional organization's regulations are not in this composer's Where it
// runs list, so that copy points at the organization instead.
export function regulationShortfallMessage(availability, { labelFor = String, organizationName = '' } = {}) {
  const shortfall = describeRegulationShortfall(availability, labelFor)
  if (organizationName) {
    return `${organizationName}: ${shortfall} Remove ${organizationName} from this post or attach a different market.`
  }
  if (!availability.partialAllowed) {
    return `${shortfall} None of the selected regulations can carry this market right now.`
  }
  const skippedCount = availability.unavailable.length + availability.unconfirmed.length
  return `${shortfall} Remove ${skippedCount === 1 ? 'it' : 'them'} from Where it runs to publish to the other regulations.`
}

// "Skipped Virginia and Arizona, which do not offer this market, and Ohio,
// which could not be confirmed."
export function describeSkippedRegulations({ unavailable = [], unconfirmed = [] } = {}, labelFor = String) {
  const clauses = []
  if (unavailable.length > 0) {
    const verb = unavailable.length === 1 ? 'does' : 'do'
    clauses.push(`${joinLabels(unavailable.map(labelFor))}, which ${verb} not offer this market`)
  }
  if (unconfirmed.length > 0) {
    clauses.push(`${joinLabels(unconfirmed.map(labelFor))}, which could not be confirmed`)
  }
  if (clauses.length === 0) return ''
  return `Skipped ${clauses.join(', and ')}.`
}

// One sentence for a response's skipped lists: the post's own, then each additional organization's.
export function describeSkippedResponse(data, { labelFor = String, organizationNameFor = String } = {}) {
  const sentences = []
  const own = describeSkippedRegulations(data?.skipped_regulations, labelFor)
  if (own) sentences.push(own)
  for (const [organizationSlug, lists] of Object.entries(data?.additional_skipped_regulations || {})) {
    const sentence = describeSkippedRegulations(lists, labelFor)
    if (sentence) sentences.push(`${organizationNameFor(organizationSlug)}: ${sentence}`)
  }
  return sentences.join(' ')
}

// Confirmation body. An unconfirmed regulation is only skipped, not blocked,
// so the copy says how to add it back.
export function partialPublishMessage({ unavailable = [], unconfirmed = [] } = {}, { labelFor = String, organizationName = '' } = {}) {
  const sentences = []
  if (unavailable.length > 0) {
    const verb = unavailable.length === 1 ? 'does' : 'do'
    sentences.push(`${joinLabels(unavailable.map(labelFor))} ${verb} not offer this market.`)
  }
  if (unconfirmed.length > 0) {
    const pronoun = unconfirmed.length === 1 ? 'it' : 'them'
    sentences.push(`${joinLabels(unconfirmed.map(labelFor))} could not be confirmed; you can add ${pronoun} back later by editing the post.`)
  }
  const body = sentences.join(' ')
  return organizationName ? `${organizationName}: ${body}` : body
}

export function partialPublishConfirmLabel({ available = [] } = {}, { organizationName = '' } = {}) {
  const count = `${available.length} regulation${available.length === 1 ? '' : 's'}`
  return organizationName ? `Publish ${organizationName} to ${count}` : `Publish to ${count}`
}

// Only an explicit false in the Bet step's validation lets Studio ask before
// the round trip; a missing entry (unchecked) is left to the server.
export function validationRegulationShortfall(validation, targetOfferings = [], outcomeIds = []) {
  if (!validation?.outcomes || targetOfferings.length === 0 || outcomeIds.length === 0) return null
  const unavailable = targetOfferings.filter((offeringId) => outcomeIds.some((outcomeId) => (
    validation.outcomes[String(outcomeId)]?.availability?.[offeringId] === false
  )))
  if (unavailable.length === 0) return null
  const available = targetOfferings.filter((offeringId) => !unavailable.includes(offeringId))
  return {
    organizationSlug: '',
    available,
    unavailable,
    unconfirmed: [],
    partialAllowed: available.length > 0,
  }
}

export function regulationNoticesByOfferingId({ unavailable = [], unconfirmed = [] } = {}) {
  return {
    ...Object.fromEntries(unconfirmed.map((offeringId) => [offeringId, REGULATION_NOTICES.unconfirmed])),
    ...Object.fromEntries(unavailable.map((offeringId) => [offeringId, REGULATION_NOTICES.unavailable])),
  }
}
